import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_SOUND_PACK,
  SOUND_PACK_STORAGE_KEY,
  SOUND_PACK_SYNC_EVENT,
  getSoundCuePath,
  getUrgentOverridePack,
  resolveSoundPack,
} from "../constants/audio";

const hasWindow = typeof window !== "undefined";
const hasAudioSupport = typeof Audio !== "undefined";

const readStoredSoundPack = () => {
  if (!hasWindow) {
    return DEFAULT_SOUND_PACK;
  }
  try {
    const stored = window.localStorage?.getItem(SOUND_PACK_STORAGE_KEY);
    return resolveSoundPack(stored);
  } catch {
    // Storage can be unavailable even when the browser exposes localStorage.
    return DEFAULT_SOUND_PACK;
  }
};

export function useSoundPack() {
  const [soundPack, setSoundPack] = useState(() => readStoredSoundPack());
  const audioCacheRef = useRef(new Map());

  useEffect(() => {
    if (!hasWindow) {
      return undefined;
    }

    const syncValue = () => {
      const next = readStoredSoundPack();
      setSoundPack((prev) => (prev === next ? prev : next));
    };

    const handleStorage = (event) => {
      if (event.key && event.key !== SOUND_PACK_STORAGE_KEY) {
        return;
      }
      syncValue();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(SOUND_PACK_SYNC_EVENT, syncValue);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(SOUND_PACK_SYNC_EVENT, syncValue);
    };
  }, []);

  const playCue = useCallback(
    (eventKey, { urgent = false } = {}) => {
      if (!eventKey || !hasAudioSupport) {
        return;
      }

      const preference = resolveSoundPack(soundPack || DEFAULT_SOUND_PACK);
      if (!urgent && preference === "no-audio") {
        return;
      }

      const playbackPack = urgent ? getUrgentOverridePack(preference) : preference;
      if (playbackPack === "no-audio") {
        return;
      }

      let source = getSoundCuePath(playbackPack, eventKey);
      if (!source && playbackPack !== DEFAULT_SOUND_PACK) {
        source = getSoundCuePath(DEFAULT_SOUND_PACK, eventKey);
      }

      if (!source) {
        return;
      }

      if (!audioCacheRef.current.has(source)) {
        const template = new Audio(source);
        template.preload = "auto";
        audioCacheRef.current.set(source, template);
      }

      const template = audioCacheRef.current.get(source);
      if (!template) {
        return;
      }

      const instance = template.cloneNode(true);
      instance.currentTime = 0;
      const playPromise = instance.play();
      if (playPromise?.catch) {
        playPromise.catch((error) => {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn("Audio playback blocked", error);
          }
        });
      }
    },
    [soundPack]
  );

  const resolvedPack = resolveSoundPack(soundPack || DEFAULT_SOUND_PACK);
  const isMuted = resolvedPack === "no-audio";

  return { soundPack: resolvedPack, isMuted, playCue };
}
